import {
  ColumnDef,
  PaginationState,
  RowSelectionState,
  Table,
} from "@tanstack/react-table"
import { DEFAULT_PAGE_SIZE } from "@tfg/ui"
import { createContext, useContext } from "react"

import { Sorts } from "~/components/SortBuilder"
import {
  CertificateByOrganizationDto,
  FindCertificatesByOrganizationId200,
  FindCertificatesByOrganizationIdRequestFiltersDto,
} from "~/gen/api"
import { CertificatesListView } from "~/pages/Brand/Certificates/constants"

type CertificatesTableContext = {
  filters: FindCertificatesByOrganizationIdRequestFiltersDto
  data: FindCertificatesByOrganizationId200
  totalCount: number
  isLoading: boolean
  isError: boolean
  error: Error | null
  view: CertificatesListView
  selectedCertificateInfo: CertificateByOrganizationDto | null
  setSelectedCertificateInfo: React.Dispatch<
    React.SetStateAction<CertificateByOrganizationDto | null>
  >
  setFilters: React.Dispatch<
    React.SetStateAction<FindCertificatesByOrganizationIdRequestFiltersDto>
  >
  unsetFilter: (
    key: keyof FindCertificatesByOrganizationIdRequestFiltersDto
  ) => void
  rowSelection: RowSelectionState
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>
  sorts: Sorts
  setSorts: React.Dispatch<React.SetStateAction<Sorts>>
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  columns: ColumnDef<CertificateByOrganizationDto>[]
  table: Table<CertificateByOrganizationDto> | null
  isColumnManagerOpen: boolean
  setIsColumnManagerOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const defaultContext: CertificatesTableContext = {
  filters: {},
  data: {
    data: [],
    count: 0,
    take: DEFAULT_PAGE_SIZE,
    skip: 0,
  },
  totalCount: 0,
  table: null,
  columns: [],
  isColumnManagerOpen: false,
  setIsColumnManagerOpen: () => undefined,
  isLoading: false,
  isError: false,
  error: null,
  view: CertificatesListView.SCOPE,
  selectedCertificateInfo: null,
  setSelectedCertificateInfo: () => undefined,
  setFilters: () => undefined,
  unsetFilter: () => undefined,
  rowSelection: {},
  setRowSelection: () => undefined,
  sorts: {},
  setSorts: () => undefined,
  pagination: {
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  setPagination: () => undefined,
  search: "",
  setSearch: () => undefined,
}

export const CertificatesTableContext =
  createContext<CertificatesTableContext>(defaultContext)

export const useCertificatesTableContext = () =>
  useContext(CertificatesTableContext)
